// Путь: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminSettings\AdminSettings.js
// Версия: 3.0.0 - Real data from system_settings
// Дата: 04.12.2025

import React, { useState, useEffect } from 'react';
import './AdminSettings.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://qr-money.onrender.com';

const CATEGORIES = {
  general: { label: 'Основное', icon: '⚙️' },
  fees: { label: 'Комиссии', icon: '💰' },
  limits: { label: 'Лимиты', icon: '📊' },
  notifications: { label: 'Уведомления', icon: '🔔' },
  security: { label: 'Безопасность', icon: '🔒' }
};

const AdminSettings = () => {
  const [settings, setSettings] = useState({});
  const [byCategory, setByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [changed, setChanged] = useState({});
  const [message, setMessage] = useState(null);

  const token = localStorage.getItem('authToken');

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setSettings(data.data || {});
        setByCategory(data.byCategory || {});
      }
    } catch (err) {
      console.error(err);
      showMessage('Ошибка загрузки', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleChange = (key, value, type) => {
    let val = value;
    if (type === 'number') val = parseFloat(value) || 0;
    if (type === 'boolean') val = value;
    
    setSettings(prev => ({ ...prev, [key]: val }));
    setChanged(prev => ({ ...prev, [key]: val }));
  };

  const handleSave = async () => {
    if (Object.keys(changed).length === 0) {
      showMessage('Нет изменений', 'info');
      return;
    }

    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(changed)
      });
      
      const data = await res.json();
      if (data.success) {
        showMessage(`Сохранено: ${data.updated?.length || 0} настроек`);
        setChanged({});
      } else {
        showMessage('Ошибка сохранения', 'error');
      }
    } catch (err) {
      showMessage('Ошибка: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!window.confirm('Отменить все изменения?')) return;
    setChanged({});
    fetchSettings();
  };

  const renderInput = (item) => {
    const value = settings[item.key];
    const isChanged = changed.hasOwnProperty(item.key);

    if (item.type === 'boolean') {
      return (
        <label className={`setting-toggle ${isChanged ? 'changed' : ''}`}>
          <input
            type="checkbox"
            checked={value || false}
            onChange={e => handleChange(item.key, e.target.checked, 'boolean')}
          />
          <span className="toggle-slider"></span>
          <span className="toggle-label">{value ? 'Вкл' : 'Выкл'}</span>
        </label>
      );
    }

    return (
      <input
        type={item.type === 'number' ? 'number' : 'text'}
        value={value ?? ''}
        onChange={e => handleChange(item.key, e.target.value, item.type)}
        className={`setting-input ${isChanged ? 'changed' : ''}`}
        step={item.type === 'number' ? '0.1' : undefined}
      />
    );
  };

  if (loading) return <div className="admin-loading">Загрузка...</div>;

  const currentCategory = byCategory[activeTab] || [];
  const changedCount = Object.keys(changed).length;

  return (
    <div className="admin-settings">
      <div className="admin-page-header">
        <h1 className="admin-page-title">⚙️ Настройки системы</h1>
        <div className="admin-page-actions">
          {changedCount > 0 && (
            <span className="changes-badge">{changedCount} изменений</span>
          )}
          <button 
            className="admin-btn" 
            onClick={handleReset}
            disabled={changedCount === 0}
          >
            ↩️ Сброс
          </button>
          <button 
            className="admin-btn admin-btn-primary" 
            onClick={handleSave}
            disabled={saving || changedCount === 0}
          >
            {saving ? '⏳ Сохранение...' : '💾 Сохранить'}
          </button>
        </div>
      </div>

      {message && (
        <div className={`settings-message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="settings-tabs">
        {Object.entries(CATEGORIES).map(([key, cat]) => {
          const items = byCategory[key] || [];
          const hasChanges = items.some(i => changed.hasOwnProperty(i.key));
          return (
            <button
              key={key}
              className={`settings-tab ${activeTab === key ? 'active' : ''} ${hasChanges ? 'has-changes' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              {cat.icon} {cat.label}
              {items.length > 0 && <span className="tab-count">{items.length}</span>}
            </button>
          );
        })}
      </div>

      {/* Settings Grid */}
      <div className="admin-card">
        <div className="settings-category-header">
          <h2>{CATEGORIES[activeTab]?.icon} {CATEGORIES[activeTab]?.label}</h2>
        </div>

        {currentCategory.length === 0 ? (
          <div className="admin-empty">Нет настроек в этой категории</div>
        ) : (
          <div className="settings-grid">
            {currentCategory.map(item => (
              <div key={item.key} className="setting-item">
                <div className="setting-info">
                  <label className="setting-label">{item.description || item.key}</label>
                  <code className="setting-key">{item.key}</code>
                </div>
                <div className="setting-control">
                  {renderInput(item)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="settings-stats">
        <div className="stat-item">
          <span className="stat-value">{Object.keys(settings).length}</span>
          <span className="stat-label">Всего настроек</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{Object.keys(byCategory).length}</span>
          <span className="stat-label">Категорий</span>
        </div>
        <div className="stat-item highlight">
          <span className="stat-value">{changedCount}</span>
          <span className="stat-label">Изменено</span>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;